import * as Sentry from '@sentry/react';
import { registerDatadogSessionTagging } from './sentry-client';

// Supports string or regexp
const ignoreErrors: string[] = ['Load failed'];

/**
 * Do not use on Astro pages, this is configured already in the Astro config file
 * Only use on Webpack entry points
 */
export function initSentry() {
  if (process.env.PUBLIC_SENTRY_DISABLED === 'true') {
    return;
  }

  // Registration has its own idempotency flag, so it must run regardless of
  // which bundle's `Sentry.init()` call ends up binding the client below —
  // tying it to that call would silently drop the tag whenever a later
  // bundle's `initSentry()` short-circuits on an already-bound client.
  // The Astro path is covered by the matching call in `O11y.tsx`, and this
  // legacy webpack entry point never runs that island, so it needs its own.
  registerDatadogSessionTagging();

  // A page can load more than one webpack bundle, and each carries its own
  // copy of this module — so a module-level flag would not catch a repeat.
  // `getClient()` reads the version-keyed carrier on `globalThis`, which every
  // bundle of the same SDK version shares, so this holds across them. Skipping
  // rather than re-initialising keeps the first client (and its already
  // registered integrations and event processors) intact.
  if (Sentry.getClient() !== undefined) {
    return;
  }

  Sentry.init({
    dsn: process.env.PUBLIC_SENTRY_DSN,
    environment: process.env.PUBLIC_HR_ENV,
    release: process.env.COMMIT_SHA,
    tracesSampleRate: 0.01, // Sets the sample rate at 1%
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.hungryroot\.com\/api/,
    ],
    // These rates are not Sentry's own defaults (its examples ship 0.1 /
    // 1.0) — they are kept explicit at 0 so replay stays off even if someone
    // adds `Sentry.replayIntegration()` back below without also touching
    // this block. Session Replay is not shipped at all right now — it is
    // the heaviest part of `@sentry/react` (~50KB gz) and, with
    // `LimitChunkCountPlugin` collapsing every webpack entry to one chunk,
    // that cost would be paid privately by each entry rather than shared.
    // Re-enable replay by re-adding the integration AND raising these rates
    // above 0.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    ignoreErrors,
    integrations: [Sentry.browserTracingIntegration()],
  });
}
